/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 09/10/2025
 * Todos los derechos reservados / All rights reserved.
 */

import React, { useEffect, useState } from "react";
import UnitsTab from "../components/tabs/UnitsTab";
import CompanyInfoTab from "../components/tabs/CompanyInfoTab";
import UserAccessRulesTab from "../components/tabs/UserAccessRulesTab";
import ApprovalSettingsTab from "../components/tabs/ApprovalSettingsTab";
import InventoryLocationsTab from "../components/tabs/InventoryLocationsTab";
import OperationClosuresTab from "../components/tabs/OperationClosuresTab";
import WhatsAppSendersTab from "../components/tabs/WhatsAppSendersTab";
import ServiceOrdersConfiguration from "./ServiceOrdersConfiguration";
import { serviceOrdersText } from "./serviceOrdersMessages";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { readStoredUser } from "../../utils/sessionUser";
import { hasPurchasingApprovalSettingsAccess, hasPurchasingSettingsPermission } from "../../utils/purchasingSettingsPermissions";
import "../PurchasingUi.css";
import "./Settings.css";

const SETTINGS_TABS = [
  { key: "company", en: "Company Info", es: "Datos de la empresa" },
  { key: "units", en: "Units of Measure", es: "Unidades de medida" },
  { key: "access", en: "User Access Rules", es: "Reglas de acceso" },
  { key: "orders", en: "Approvals & Permissions", es: "Aprobaciones y permisos" },
  { key: "services", en: "Service departments", es: "Departamentos de servicios" },
  { key: "locations", en: "Inventory Locations", es: "Ubicaciones de inventario" },
  { key: "operation_closures", en: "Operations closure", es: "Cierre de operaciones" },
  { key: "whatsapp", en: "WhatsApp senders", es: "Remitentes de WhatsApp" },
];

export default function Settings({ forcedTab = null }) {
  const { themeId, languageId } = usePmsTheme();
  const isSpanish = languageId === "es";
  const [, refreshSession] = useState(0);
  const currentUser = readStoredUser();
  useEffect(() => {
    const update = () => refreshSession((version) => version + 1);
    window.addEventListener("palantir-session-updated", update);
    window.addEventListener("storage", update);
    return () => { window.removeEventListener("palantir-session-updated", update); window.removeEventListener("storage", update); };
  }, []);
  const permissions = currentUser?.permissions || {};
  const isAdminRole = Number(currentUser?.id) === 1;
  const canConfigureServices = isAdminRole || permissions["purchasing.services.configure"] === true;
  const canViewApprovalSettings = hasPurchasingApprovalSettingsAccess(currentUser);
  const businessUnitId = Number(currentUser?.active_business_unit_id || 0);
  const serviceText = serviceOrdersText(languageId);

  const tabPermissions = {
    units: hasPurchasingSettingsPermission(currentUser, "units", "view"),
    company: hasPurchasingSettingsPermission(currentUser, "company", "view"),
    access: hasPurchasingSettingsPermission(currentUser, "access", "view"),
    orders: canViewApprovalSettings || canConfigureServices,
    services: canConfigureServices,
    locations: hasPurchasingSettingsPermission(currentUser, "locations", "view"),
    operation_closures: hasPurchasingSettingsPermission(currentUser, "operation_closures", "view"),
    whatsapp: hasPurchasingSettingsPermission(currentUser, "whatsapp", "view"),
  };

  const defaultTab = SETTINGS_TABS.find((tab) => tabPermissions[tab.key])?.key || null;

  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const activeTab = forcedTab || selectedTab;
  const isSidebarMode = Boolean(forcedTab);
  const activeTabAllowed = activeTab ? Boolean(tabPermissions[activeTab]) : false;

  useEffect(() => {
    if (forcedTab) return;
    if (activeTabAllowed) return;
    setSelectedTab(defaultTab);
  }, [activeTabAllowed, defaultTab, forcedTab]);

  if (!activeTab || !activeTabAllowed) {
    return (
      <div className="pilcore-purchasing-ui purchSettings__notice" data-theme={themeId} role="alert">
        {isSpanish ? "No tienes permiso para acceder a esta sección." : "You don't have permission to access this section."}
      </div>
    );
  }

  return (
    <div className="pilcore-purchasing-ui purchSettings" data-theme={themeId}>
      {!isSidebarMode && (
        <nav className="purchSettings__tabs"
          aria-label={isSpanish ? "Configuración de compras" : "Purchasing settings"}>
          {SETTINGS_TABS.filter((tab) => tabPermissions[tab.key]).map((tab) => (
            <button key={tab.key} type="button" aria-pressed={activeTab === tab.key}
              onClick={() => setSelectedTab(tab.key)}>
              {isSpanish ? tab.es : tab.en}
            </button>
          ))}
        </nav>
      )}

      <div className="purchSettings__content">
        {activeTab === "services" ? <section className="purchSettings__services">
          {Number.isSafeInteger(businessUnitId) && businessUnitId > 0
            ? <ServiceOrdersConfiguration businessUnitId={businessUnitId} />
            : <div role="alert">{serviceText("Select an active business unit before continuing.")}</div>}
        </section> : activeTab === "units" ? <UnitsTab /> : activeTab === "access" ? <UserAccessRulesTab /> : activeTab === "orders" ? <ApprovalSettingsTab canManagePurchasing={canViewApprovalSettings} canConfigureServices={canConfigureServices} businessUnitId={businessUnitId} /> : activeTab === "locations" ? <InventoryLocationsTab /> : activeTab === "operation_closures" ? <OperationClosuresTab /> : activeTab === "whatsapp" ? <WhatsAppSendersTab /> : <CompanyInfoTab />}
      </div>
    </div>
  );
}
